<template>
  <b-section-loader :loading="$loading.isLoading('roleSingle')">
    <v-card border rounded="lg">
      <v-card-text class="pa-6">
        <div class="d-flex align-start justify-space-between ga-4 flex-wrap">
          <div class="flex-grow-1">
            <div class="d-flex align-center ga-2 flex-wrap mb-4">
              <h2 class="font-size-20">
                {{ role.name }}
              </h2>
              <span class="text-disabled">
                {{ role.description || 'Опис ролі не вказано' }}
              </span>
              <v-btn
                aria-label="Редагувати роль"
                border
                density="comfortable"
                icon="mdi-pencil-outline"
                rounded="lg"
                size="small"
                variant="text"
                @click="openRoleDialog"></v-btn>
            </div>
          </div>

          <v-btn
            :loading="$loading.isLoading('roleUpdate')"
            color="grey-darken-4"
            prepend-icon="mdi-check"
            @click="submit">
            Зберегти
          </v-btn>
        </div>

        <div class="d-flex align-center ga-2 mt-2 mb-3">
          <span class="text-uppercase font-weight-bold text-disabled">
            Область даних
          </span>
          <span
            v-tooltip:top="dataScopeTooltip"
            class="d-inline-flex align-center">
            <v-icon
              color="blue-lighten-3"
              icon="mdi-information-outline"></v-icon>
          </span>
        </div>

        <div class="d-flex flex-column ga-3 mb-4">
          <v-card
            v-for="scope in dataScopeItems"
            :key="scope.value"
            :class="{
              'role-scope-card--active': role.data_scope === scope.value,
            }"
            class="role-scope-card"
            rounded="lg"
            @click="role.data_scope = scope.value">
            <v-card-text class="d-flex align-start ga-3 pa-0">
              <span
                :class="{
                  'role-radio--active': role.data_scope === scope.value,
                }"
                class="role-radio">
                <span></span>
              </span>
              <div>
                <div
                  :class="{
                    'role-scope-title--active': role.data_scope === scope.value,
                  }"
                  class="role-scope-title">
                  {{ scope.title }}
                </div>
                <div class="role-scope-subtitle">
                  {{ scope.subtitle }}
                </div>
              </div>
            </v-card-text>
          </v-card>
        </div>

        <v-divider class="my-4"></v-divider>

        <div class="d-flex align-center mb-4">
          <h3 class="font-size-18">Права доступу</h3>

          <v-spacer></v-spacer>

          <v-btn
            color="blue-darken-3"
            size="small"
            variant="text"
            @click="selectAll">
            <span class="font-weight-bold">Обрати всі</span>
          </v-btn>

          <v-btn
            class="ml-2"
            color="grey-darken-1"
            size="small"
            variant="text"
            @click="deselectAll">
            <span class="font-weight-bold">Зняти всі</span>
          </v-btn>
        </div>

        <div class="d-flex flex-column ga-3">
          <v-card
            v-for="(section, index) in permissionSections"
            :key="section.label"
            class="role-permission-card overflow-hidden"
            rounded="lg">
            <simple-expansion-panel
              :expanded="index === 0"
              class="role-permission-panel">
              <template #label>
                <div class="d-flex align-center w-100">
                  <b-checkbox
                    class="role-permission-checkbox"
                    color="blue-darken-3"
                    data-stub="RoleSectionCheckbox"
                    density="compact"
                    hide-details
                    :data-checked="String(Boolean(isSectionSelected(section)))"
                    :data-indeterminate="
                      String(Boolean(isSectionIndeterminate(section)))
                    "
                    :indeterminate="isSectionIndeterminate(section)"
                    :model-value="isSectionSelected(section)"
                    @click.stop
                    @update:model-value="
                      toggleSection(section, Boolean($event))
                    "></b-checkbox>

                  <div>
                    <div class="role-permission-title">
                      {{ section.label }}
                    </div>
                    <div class="role-permission-subtitle">
                      {{ getSectionSelectedCount(section) }} з
                      {{ getSectionPermissionsCount(section) }} прав
                    </div>
                  </div>
                </div>
              </template>

              <template #action="{ isExpanded }">
                <v-btn icon size="small" variant="plain">
                  <v-icon
                    color="#9898a8"
                    size="x-small"
                    :icon="
                      isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'
                    "></v-icon>
                </v-btn>
              </template>

              <template #default>
                <v-divider></v-divider>
                <v-card-text class="pa-4">
                  <v-row>
                    <v-col
                      v-for="group in section.groups"
                      :key="group.key"
                      cols="12"
                      lg="3"
                      md="4"
                      sm="6">
                      <v-card
                        class="role-permission-group-card h-100"
                        rounded="lg">
                        <div class="d-flex align-center mb-3">
                          <b-checkbox
                            class="role-permission-checkbox"
                            color="blue-darken-3"
                            data-stub="RoleGroupCheckbox"
                            density="compact"
                            hide-details
                            :data-checked="
                              String(Boolean(isGroupSelected(group.key)))
                            "
                            :data-indeterminate="
                              String(Boolean(isGroupIndeterminate(group.key)))
                            "
                            :indeterminate="isGroupIndeterminate(group.key)"
                            :model-value="isGroupSelected(group.key)"
                            @click.stop
                            @update:model-value="
                              toggleGroup(group, Boolean($event))
                            "></b-checkbox>
                          <div class="role-permission-title">
                            {{ group.label }}
                          </div>
                        </div>

                        <v-divider class="mb-3"></v-divider>

                        <div
                          v-for="perm in group.permissions"
                          :key="perm.id"
                          class="role-permission-item">
                          <b-checkbox
                            v-model="selectedPermissions"
                            class="role-permission-checkbox"
                            color="blue-darken-3"
                            data-stub="RolePermissionCheckbox"
                            density="compact"
                            hide-details
                            :data-checked="
                              String(selectedPermissions.includes(perm.name))
                            "
                            :data-label="getActionLabel(perm)"
                            :data-value="perm.name"
                            :label="getActionLabel(perm)"
                            :value="perm.name"></b-checkbox>
                        </div>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-card-text>
              </template>
            </simple-expansion-panel>
          </v-card>
        </div>
      </v-card-text>
    </v-card>
  </b-section-loader>
</template>

<script lang="ts">
import SimpleExpansionPanel from '@/components/expansion-panel/SimpleExpansionPanel.vue'
import { BCheckbox, BSectionLoader } from 'best-modules/components'
import { initRole } from './role'

export default {
  name: 'RoleSingle',
  components: {
    BCheckbox,
    BSectionLoader,
    SimpleExpansionPanel,
  },
  setup() {
    const dataScopeTooltip =
      'Область даних - визначає, до яких документів матиме доступ ' +
      'користувач із цією роллю - від лише власних до всіх у системі.'

    return {
      ...initRole(),
      dataScopeTooltip,
    }
  },
}
</script>

<style lang="scss" scoped>
.role-scope-card {
  background: #fff;
  border: 1.5px solid #e6e8ef;
  cursor: pointer;
  padding: 12px 15px;
  transition: all 0.12s;
}

.role-scope-card--active {
  background: #f0f6fb;
  border-color: #185fa5;
}

.role-radio {
  align-items: center;
  border: 2px solid #d2d5df;
  border-radius: 50%;
  display: inline-flex;
  flex: 0 0 18px;
  height: 18px;
  justify-content: center;
  margin-top: 1px;
  width: 18px;
}

.role-radio--active {
  border-color: #185fa5;

  span {
    background: #185fa5;
    border-radius: 50%;
    height: 9px;
    width: 9px;
  }
}

.role-scope-title {
  color: #1a1a1b;
  display: block;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
}

.role-scope-title--active {
  color: #185fa5;
}

.role-scope-subtitle {
  color: #9898a8;
  display: block;
  font-size: 13px;
  line-height: 1.45;
  margin-top: 2px;
}

.role-permission-card {
  border: 1px solid #e6e8ef;
}

.role-permission-panel :deep(> .d-flex.align-center.justify-space-between) {
  cursor: pointer;
  gap: 12px;
  margin-bottom: 0 !important;
  padding: 14px 16px;
}

.role-permission-group-card {
  border: 1px solid #e6e8ef;
  padding: 16px;
}

.role-permission-title {
  color: #1a1a1b;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.25;
}

.role-permission-subtitle {
  color: #9898a8;
  font-size: 13px;
  line-height: 1.25;
  margin-top: 1px;
}

.role-permission-item {
  min-height: 28px;
}

.role-permission-checkbox {
  flex: 0 0 auto;
}

.role-permission-checkbox :deep(.v-selection-control) {
  min-height: 24px;
}

.role-permission-checkbox :deep(.v-selection-control__input) {
  height: 24px;
  width: 24px;
}

.role-permission-checkbox :deep(.v-icon) {
  border-radius: 6px;
  font-size: 25px;
}
</style>
